import Container from '@/components/Container';
import SkuCardSellerModalContent from '@/components/common/cards/sku/SkuCardSellerModalContent';
import ResponsiveComponent from '@/components/common/ResponsiveComponent';
import LandingPage from '@/components/landing-pages/LandingPage';
import StoreMap from '@/components/landing-pages/StoreMap';
import SellerSearchLayout from '@/components/skus/SellerSearchLayout';
import SellerDesktop from '@/components/skus/SellerPage/SellerDesktop';
import SellerMobile from '@/components/skus/SellerPage/SellerMobile/SellerMobile';
import ShopStructuredData from '@/components/structured_data/ShopStructuredData';
import { SORT_TOKENS, virtualIndex } from '@/constants/search.constants';
import cookiesConstants from '@/constants/cookies.constants';
import { useSpiritoryModal } from '@/context/SpiritoryModalProvider';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import useWindowWidth from '@/hooks/useWindowWidth';
import type { SkuDTO } from '@/types/api/sku';
import { AllSortsCustomTranslated } from '@/types/other';
import { SellerPageProps } from '@/types/props/pages';
import { CommonEnum } from '@/types/translations/CommonEnum';
import getCanonicalUrl from '@/utilites/seo/get-canonical-url';
import { getCookie } from 'cookies-next';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

export const createSortOptions = (
  translate: (key: CommonEnum) => string
): AllSortsCustomTranslated[] => [
  {
    label: translate(CommonEnum.most_popular),
    value: virtualIndex(SORT_TOKENS.mostPopular),
    name: translate(CommonEnum.most_popular),
  },
  {
    label: translate(CommonEnum.most_watched),
    value: virtualIndex(SORT_TOKENS.mostWatched),
    name: translate(CommonEnum.most_watched),
  },
  {
    label: translate(CommonEnum.newest),
    value: virtualIndex(SORT_TOKENS.newest),
    name: translate(CommonEnum.newest),
  },
  {
    label: translate(CommonEnum.lowest_ask_sort),
    value: virtualIndex(SORT_TOKENS.lowestAskAsc),
    name: translate(CommonEnum.lowest_ask_sort),
  },
  {
    label: translate(CommonEnum.highest_ask),
    value: virtualIndex(SORT_TOKENS.highestAskDesc),
    name: translate(CommonEnum.highest_ask),
  },
  {
    label: translate(CommonEnum.lowest_bid),
    value: virtualIndex(SORT_TOKENS.lowestBidAsc),
    name: translate(CommonEnum.lowest_bid),
  },
  {
    label: translate(CommonEnum.highest_bid),
    value: virtualIndex(SORT_TOKENS.highestBidDesc),
    name: translate(CommonEnum.highest_bid),
  },
];

/**
 * A landing page that shows one seller's offers instead of the whole catalogue.
 *
 * Used by `/seller/<slug>` and — since WEB-1322 — by any `/shop/<slug>` landing
 * page whose Strapi entry carries a `seller_id`, which is how the Munich shop
 * page lists only what is physically on its shelf.
 */
export default function SellerLandingPage({
  asks,
  landingPage,
  localeAlternates,
}: SellerPageProps): JSX.Element {
  const { translate } = useTypedTranslation<CommonEnum>('common');
  const modal = useSpiritoryModal();
  const windowWidth = useWindowWidth();
  const router = useRouter();

  const name = landingPage?.name_string || 'Unknown Seller';

  // Extract SKU slugs from seller's asks data
  const sellerSkuSlugs = useMemo(() => {
    if (!asks || asks.length === 0) {
      return [];
    }

    return [...new Set(asks.map(ask => ask.sku.slug))];
  }, [asks]);

  // Create sort options
  const allSortsCustomTranslated = useMemo(() => createSortOptions(translate), [translate]);

  // Check if filters should be open on mobile
  const hasFilterCookie = getCookie(cookiesConstants.COOKIES.FILTERS_ARE_OPEN.name) === 'true';

  // Handle seller card click to open modal
  const handleSellerCardClick = (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
    sku: SkuDTO,
    groupedAsks?: any
  ) => {
    e.preventDefault();

    // Use the grouped asks data if available, otherwise find the ask data
    const askData = groupedAsks?.[0] || asks?.find(ask => ask.sku.slug === sku.slug);
    if (askData) {
      modal.initModal({
        title: translate(CommonEnum.ask_details) || 'Ask Details',
        body: (
          <SkuCardSellerModalContent
            sku={sku}
            askValue={askData.value}
            sellerName={name}
            askId={askData.id}
          />
        ),
        type: 'normal',
        showButtons: false,
        showCancelButton: false,
        showSubmitButton: false,
        submitAction: () => modal.closeModal(),
        cancelAction: () => modal.closeModal(),
      });
    }
  };

  return (
    <div className="tw-min-h-screen">
      <ShopStructuredData
        sellerId={landingPage?.seller_id}
        faqs={landingPage?.faqs}
        pageUrl={getCanonicalUrl(router)}
      />
      <LandingPage
        landingPage={landingPage}
        /* Renders only for a landing page with a real shop behind it, below the
           copy — the article does the convincing, the card says where to go. */
        afterContent={<StoreMap sellerId={landingPage?.seller_id} />}
      >
        {sellerSkuSlugs.length > 0 ? (
          <SellerSearchLayout
            sellerName={name}
            skuSlugs={sellerSkuSlugs}
            localeAlternates={localeAlternates}
            sellerId={landingPage?.seller_id}
            metaTitle={landingPage?.meta_title}
            metaDescription={landingPage?.meta_description}
            metaKeywords={landingPage?.meta_keywords}
          >
            <ResponsiveComponent
              windowWidth={windowWidth}
              DesktopComponent={
                <SellerDesktop
                  allSortsCustomTranslated={allSortsCustomTranslated}
                  asks={asks}
                  handleSellerCardClick={handleSellerCardClick}
                  name={name}
                  filterConfig={{ status: false }}
                />
              }
              MobileComponent={
                <SellerMobile
                  allSortsCustomTranslated={allSortsCustomTranslated}
                  hasFilterCookie={hasFilterCookie}
                  asks={asks}
                  onCardClick={handleSellerCardClick}
                  sellerName={name}
                />
              }
            />
          </SellerSearchLayout>
        ) : (
          <Container className="tw-md:my-5 tw-mb-5 tw-max-w-[1280px] tw-pt-4">
            <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
              <p className="tw-text-center tw-text-gray-500">
                No offers available from this seller.
              </p>
            </div>
          </Container>
        )}
      </LandingPage>
    </div>
  );
}
